import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { trackSignUp, track, EVENTS } from "@/lib/analytics";
import { Sparkles, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

/**
 * InArticleCta — high-intent lead capture dropped mid-article.
 *
 * Readers of behavior/crisis articles are at their most motivated moment.
 * This card converts that intent with the same frictionless email-only
 * signup as /beta, attributed via utm_campaign=article_<slug> so the
 * Link performance table + GA4 show exactly which article converted.
 * Hidden for already-signed-in users.
 */
const GROUP_COPY = {
    behavior: "Dealing with this right now? Lane can walk you through it — step by step, free.",
    "hard-conversations": "Dreading this conversation? Lane helps you find the words — free.",
    "paying-for-care": "Confused about who pays? Lane maps your benefits in minutes — free.",
    resources: "Not sure where to start? Lane builds your personal care plan — free.",
    learn: "Want guidance specific to your family? Lane personalizes all of this — free.",
    guide: "Want help checking your GUIDE eligibility? Lane does it with you — free.",
};

export const InArticleCta = ({ slug, group, title = "", lanePrompt = "" }) => {
    const [email, setEmail] = useState("");
    const [state, setState] = useState("idle"); // idle | busy | done | error
    const { refresh } = useAuth();
    // Frozen at mount — otherwise the token we set on successful signup
    // makes this true on re-render and hides the success message.
    const [signedIn] = useState(() => {
        try { return Boolean(localStorage.getItem("mlcn_token")); } catch { return false; }
    });
    if (signedIn) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || state === "busy") return;
        track(EVENTS.SIGNUP_STARTED, { form: "in_article_cta", article_slug: slug });
        setState("busy");
        try {
            // Secure passwordless — sends a 6-digit code + one-tap link; the
            // account is created only after verification on /login or /magic.
            await api.post("/auth/passwordless/start", {
                email: email.trim(),
                utm_campaign: `article_${slug}`.slice(0, 64),
            });
            setState("done");
        } catch (err) {
            if (err?.response?.status === 429) {
                // Code already in flight — still a success from the reader's view.
                setState("done");
                return;
            }
            setState("error");
        }
    };

    return (
        <aside
            className="not-prose my-8 rounded-2xl border border-teal-light bg-gradient-to-br from-teal-soft/50 via-white to-cream-light p-5 md:p-6"
            data-testid="in-article-cta"
        >
            {state === "done" ? (
                <div className="flex items-start gap-3" data-testid="in-article-cta-success">
                    <CheckCircle2 className="text-teal-dark shrink-0 mt-0.5" size={22} strokeWidth={1.8} />
                    <div>
                        <p className="font-heading text-lg text-[#1F2624]">Check your email — your sign-in code is on the way.</p>
                        <p className="text-sm text-[#4C5551] mt-1">We sent a 6-digit code and a one-tap sign-in link to <strong>{email.trim()}</strong>. Both expire in 10 minutes.</p>
                        <div className="flex flex-wrap items-center gap-3 mt-3">
                            <Link
                                to={`/login?email=${encodeURIComponent(email.trim().toLowerCase())}`}
                                state={{ codeSent: true }}
                                onClick={() => {
                                    try {
                                        localStorage.setItem("ml_lane_seed_prompt",
                                            lanePrompt || `I just read about ${title || "this topic"} — help me figure out what to do for my loved one.`);
                                    } catch { /* private mode */ }
                                }}
                                data-testid="in-article-cta-enter-code"
                                className="ml-btn-primary inline-flex items-center gap-2 !min-h-[44px] !py-2 !px-5 text-sm"
                            >
                                Enter your code <ArrowRight size={15} />
                            </Link>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-2xl bg-teal-dark text-white flex items-center justify-center shrink-0">
                            <Sparkles size={16} strokeWidth={1.8} />
                        </div>
                        <div className="min-w-0">
                            <p className="font-heading text-lg md:text-xl text-[#1F2624] leading-snug">
                                {GROUP_COPY[group] || GROUP_COPY.learn}
                            </p>
                            <p className="text-sm text-[#4C5551] mt-1">
                                Free to start · no credit card · takes 30 seconds.
                            </p>
                        </div>
                    </div>
                    <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Your email"
                            data-testid="in-article-cta-email"
                            className="ml-input flex-1 !min-h-[48px]"
                            autoComplete="email"
                        />
                        <button
                            type="submit"
                            disabled={state === "busy"}
                            data-testid="in-article-cta-submit"
                            className="ml-btn-primary inline-flex items-center justify-center gap-2 !min-h-[48px] !px-5 text-sm disabled:opacity-60"
                        >
                            {state === "busy" ? <Loader2 size={16} className="animate-spin" /> : null}
                            Get free help
                        </button>
                    </form>
                    {state === "error" && (
                        <p className="text-xs text-coral-dark mt-2" data-testid="in-article-cta-error">
                            Something went wrong — try again, or use the Sign in link in the header.
                        </p>
                    )}
                </>
            )}
        </aside>
    );
};

export default InArticleCta;
